import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../../services/api';
import { 
  Users, 
  Search, 
  RefreshCw, 
  Download,
  Mail,
  Phone,
  Building2,
  Calendar,
  FileText,
  Loader,
  FileDown
} from 'lucide-react';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${({ $variant, theme }) => $variant === 'primary' ? `
    background: ${theme.colors.primary};
    color: white;
    border: none;
    
    &:hover {
      background: ${theme.colors.primaryDark};
    }
  ` : `
    background: white;
    color: ${theme.colors.textPrimary};
    border: 1px solid ${theme.colors.gray200};
    
    &:hover {
      background: ${theme.colors.gray50};
    }
  `}
`;

const SearchBox = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  
  input {
    flex: 1;
    padding: ${({ theme }) => theme.spacing.md};
    border: 1px solid ${({ theme }) => theme.colors.gray200};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    
    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const TableContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.gray50};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Tr = styled.tr`
  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
  }
`;

const Badge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  
  ${({ $status }) => {
    switch ($status) {
      case 'APROBADO':
        return 'background: #dcfce7; color: #166534;';
      case 'PENDIENTE':
        return 'background: #fef9c3; color: #854d0e;';
      case 'RECHAZADO':
        return 'background: #fee2e2; color: #991b1b;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  svg {
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  h3 {
    margin: 0 0 8px 0;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const InfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const PropuestasCount = styled.span<{ $hasProposals: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border-radius: 14px;
  font-weight: 600;
  font-size: 13px;
  
  ${({ $hasProposals, theme }) => $hasProposals ? `
    background: ${theme.colors.primary}15;
    color: ${theme.colors.primary};
  ` : `
    background: ${theme.colors.gray100};
    color: ${theme.colors.textMuted};
  `}
`;

const DocLink = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  cursor: pointer;
  padding: 2px 0;
  text-decoration: underline;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:disabled {
    color: ${({ theme }) => theme.colors.textMuted};
    cursor: not-allowed;
    text-decoration: none;
  }
`;

interface Proveedor {
  id: number;
  proveedor_nombre: string;
  proveedor_nit: string;
  username: string;
  contacto1_nombre: string;
  contacto1_correo: string;
  contacto1_celular: string;
  anios_experiencia: string;
  numero_vehiculos: number;
  estado: string;
  fecha_registro: string;
  fecha_actualizacion: string;
  total_propuestas: number;
  brochure_url: string | null;
  plan_descarbonizacion_url: string | null;
  soporte_otra_certificacion: string | null;
  anexar_soportes_url: string | null;
}

const ProveedoresModule: React.FC = () => {
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const cargarProveedores = async () => {
    setLoading(true);
    try {
      const response = await api.get('/proveedores');
      setProveedores(response.data.proveedores || []);
    } catch (error) {
      console.error('Error cargando proveedores:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarProveedores();
  }, []);

  const proveedoresFiltrados = proveedores.filter(p => 
    p.proveedor_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.proveedor_nit?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.contacto1_correo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const descargarExcel = () => {
    const headers = ['Nombre', 'NIT', 'Usuario', 'Contacto', 'Correo', 'Teléfono', 'Experiencia', 'Vehículos', 'Propuestas', 'Estado', 'Fecha Registro'];
    const rows = proveedoresFiltrados.map(p => [
      p.proveedor_nombre || '',
      p.proveedor_nit || '',
      p.username || '',
      p.contacto1_nombre || '',
      p.contacto1_correo || '',
      p.contacto1_celular || '',
      p.anios_experiencia || '',
      p.numero_vehiculos || 0,
      p.total_propuestas || 0,
      p.estado || '',
      p.fecha_registro ? new Date(p.fecha_registro).toLocaleDateString() : ''
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `proveedores_comex_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const descargarDocumento = async (blobName: string) => {
    // External URLs open directly in a new tab instead of downloading from GCS
    if (blobName.startsWith('http://') || blobName.startsWith('https://')) {
      window.open(blobName, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const response = await api.get('/admin/descargar-documento', {
        params: { blob_name: blobName },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', blobName.split('/').pop() || 'documento');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando documento:', error);
      alert('Error al descargar el documento');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Container>
      <Header>
        <Title>
          <Users size={28} />
          Proveedores Registrados
        </Title>
        <Actions>
          <Button onClick={descargarExcel}>
            <Download size={18} />
            Descargar Excel
          </Button>
          <Button onClick={cargarProveedores}>
            <RefreshCw size={18} />
            Actualizar
          </Button>
        </Actions>
      </Header>

      <SearchBox>
        <SearchInput>
          <Search size={20} color="#64748b" />
          <input
            type="text"
            placeholder="Buscar por nombre, NIT, usuario o correo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
      </SearchBox>

      <TableContainer>
        {loading ? (
          <LoadingContainer>
            <Loader size={32} />
            <span style={{ marginLeft: '12px' }}>Cargando proveedores...</span>
          </LoadingContainer>
        ) : proveedoresFiltrados.length === 0 ? (
          <EmptyState>
            <Users size={48} />
            <h3>No hay proveedores registrados</h3>
            <p>Los proveedores aparecerán aquí cuando se registren en el sistema.</p>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Proveedor</Th>
                <Th>Contacto</Th>
                <Th>Flota</Th>
                <Th>Experiencia</Th>
                <Th>Propuestas</Th>
                <Th>Documentos</Th>
                <Th>Registro</Th>
              </tr>
            </thead>
            <tbody>
              {proveedoresFiltrados.map((proveedor) => (
                <Tr key={proveedor.id}>
                  <Td>
                    <InfoCell>
                      <strong>{proveedor.proveedor_nombre || 'Sin nombre'}</strong>
                      <InfoRow>
                        <Building2 />
                        NIT: {proveedor.proveedor_nit || 'N/A'}
                      </InfoRow>
                      <InfoRow style={{ color: '#008752' }}>
                        @{proveedor.username}
                      </InfoRow>
                    </InfoCell>
                  </Td>
                  <Td>
                    <InfoCell>
                      <span>{proveedor.contacto1_nombre || '-'}</span>
                      {proveedor.contacto1_correo && (
                        <InfoRow>
                          <Mail />
                          {proveedor.contacto1_correo}
                        </InfoRow>
                      )}
                      {proveedor.contacto1_celular && (
                        <InfoRow>
                          <Phone />
                          {proveedor.contacto1_celular}
                        </InfoRow>
                      )}
                    </InfoCell>
                  </Td>
                  <Td>
                    <InfoCell>
                      <span><strong>{proveedor.numero_vehiculos || 0}</strong> vehículos</span>
                    </InfoCell>
                  </Td>
                  <Td>{proveedor.anios_experiencia || '-'}</Td>
                  <Td>
                    <PropuestasCount $hasProposals={proveedor.total_propuestas > 0}>
                      <FileText size={14} style={{ marginRight: '4px' }} />
                      {proveedor.total_propuestas || 0}
                    </PropuestasCount>
                  </Td>
                  <Td>
                    <InfoCell>
                      {proveedor.brochure_url && (
                        <DocLink onClick={() => descargarDocumento(proveedor.brochure_url!)}>
                          <FileDown size={12} /> Brochure
                        </DocLink>
                      )}
                      {proveedor.plan_descarbonizacion_url && (
                        <DocLink onClick={() => descargarDocumento(proveedor.plan_descarbonizacion_url!)}>
                          <FileDown size={12} /> Plan Descarbonización
                        </DocLink>
                      )}
                      {proveedor.soporte_otra_certificacion && (
                        <DocLink onClick={() => descargarDocumento(proveedor.soporte_otra_certificacion!)}>
                          <FileDown size={12} /> Otra Certificación
                        </DocLink>
                      )}
                      {proveedor.anexar_soportes_url && (
                        <DocLink onClick={() => descargarDocumento(proveedor.anexar_soportes_url!)}>
                          <FileDown size={12} /> Soportes
                        </DocLink>
                      )}
                      {!proveedor.brochure_url && !proveedor.plan_descarbonizacion_url && 
                       !proveedor.soporte_otra_certificacion && !proveedor.anexar_soportes_url && (
                        <span style={{ color: '#999', fontSize: '12px' }}>Sin documentos</span>
                      )}
                    </InfoCell>
                  </Td>
                  <Td>
                    <InfoRow>
                      <Calendar />
                      {formatDate(proveedor.fecha_registro)}
                    </InfoRow>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>
    </Container>
  );
};

export default ProveedoresModule;

